/**
 * Sector route registration — composition shell wires URL params/search to
 * SectorRoute (plan §3.4: router.tsx is not edited in 5/6/7).
 */
import { createRoute, useNavigate } from '@tanstack/react-router';
import { lazy, Suspense, useCallback, type JSX } from 'react';
import { z } from 'zod';

import {
  SectorIdSchema,
  SectorSearchParamsSchema,
  type SectorSearchParams,
  type Ticker,
} from '@meridian/contracts';

import { Skeleton } from '@/components/primitives/skeleton';
import { rootRoute } from '@/router-root';

import { InvalidSectorParamError } from './invalid-sector-param-error';
import { SectorRouteError } from './sector-route-error';

const loadSectorRoute = () => import('./sector-route');

const SectorScreen = lazy(async () => {
  const module = await loadSectorRoute();
  return { default: module.SectorRoute };
});

const paramsSchema = z.object({
  sectorId: SectorIdSchema,
});

/** Stock detail navigation target — preserves win/idx from the sector screen. */
export function stockDetailTarget(
  ticker: Ticker,
  search: Pick<SectorSearchParams, 'win' | 'idx'>,
): {
  readonly to: '/stock/$ticker';
  readonly params: { readonly ticker: Ticker };
  readonly search: {
    readonly win: SectorSearchParams['win'];
    readonly idx: SectorSearchParams['idx'];
  };
} {
  return {
    to: '/stock/$ticker',
    params: { ticker },
    search: { win: search.win, idx: search.idx },
  };
}

function SectorRouteAdapter(): JSX.Element {
  const { sectorId } = sectorRoute.useParams();
  const search = sectorRoute.useSearch();
  const navigate = useNavigate();

  const onTickerSelect = useCallback(
    (ticker: Ticker) => {
      void navigate(stockDetailTarget(ticker, { win: search.win, idx: search.idx }));
    },
    [navigate, search.win, search.idx],
  );

  return (
    <Suspense fallback={<Skeleton height="18rem" label="Loading sector" />}>
      <SectorScreen
        sectorId={sectorId}
        window={search.win}
        index={search.idx}
        onTickerSelect={onTickerSelect}
      />
    </Suspense>
  );
}

export const sectorRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sector/$sectorId',
  params: {
    parse: (raw) => {
      const result = paramsSchema.safeParse(raw);
      if (!result.success) {
        /* Carries the sentinel the error boundary discriminates on. Throwing the
           raw ZodError instead is what let the router's default fallback render
           the whole internal sector-id enum as page content. */
        throw new InvalidSectorParamError();
      }
      return result.data;
    },
    stringify: (p) => ({ sectorId: p.sectorId }),
  },
  validateSearch: (search: Record<string, unknown>) => SectorSearchParamsSchema.parse(search),
  loader: loadSectorRoute,
  component: SectorRouteAdapter,
  errorComponent: SectorRouteError,
});
